import {useState, useEffect, useCallback} from 'react';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useWebsocket} from 'context/websocket';
import {useIVRemoteSync, getIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';
import {useGuest} from 'hooks';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {WS_EVENT_TYPES} from '@chekin/api';

export const useEventListener = () => {
  const id = useGetGuestId();
  const {guest} = useGuest({id});
  const [isStartedOnMobile, setStartedOnMobile] = useState<boolean>();
  const {loadRemoteState} = useIVRemoteSync();
  const {goToGuestForm, goToHome, goToGuestRegistration} = useRemoteIVFlow();
  const websocket = useWebsocket();

  const handleStartedOnMobile = useCallback(() => setStartedOnMobile(true), []);

  const handleReturningToDesktop = useCallback(() => {
    if (guest) {
      goToGuestRegistration();
    } else {
      void loadRemoteState({postHandler: goToGuestForm});
    }
  }, [goToGuestForm, goToGuestRegistration, guest, loadRemoteState]);

  const handleContinueOnMobile = useCallback(() => goToHome(), [goToHome]);

  useEffect(() => {
    const message = websocket.message;
    const intentId = getIVIntentRemoteSession();
    if (!message?.event_type || intentId !== message.instance_id) return;

    if (WS_EVENT_TYPES.ivStartedOnMobile === message.event_type) {
      handleStartedOnMobile();
    }

    if (WS_EVENT_TYPES.ivCompletedContinuedOnMobile === message.event_type) {
      handleContinueOnMobile();
    }

    if (WS_EVENT_TYPES.ivCompletedReturningToDesktop === message.event_type) {
      handleReturningToDesktop();
    }

    return () => {
      websocket.clearMessage();
    };
  }, [
    handleContinueOnMobile,
    handleReturningToDesktop,
    handleStartedOnMobile,
    websocket,
  ]);

  return {
    handleStartedOnMobile,
    handleReturningToDesktop,
    handleContinueOnMobile,
    isStartedOnMobile,
  };
};
